import { motion, useInView } from 'framer-motion';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { useEffect, useRef, useState } from 'react';

import Header from '@/common/header';
import { bai_jamjuree } from '@/public/assets/fonts/Bai-Jamjuree';
import { outfit } from '@/public/assets/fonts/Outfit';

import { CreateAccountModal } from '../../create-account-modal';
import { Footer } from '../../footer';
import { FaqAccordion } from '../../shared/faq-accordion';
import type { DefaultTrialProps } from '../../trial-layout-switcher';
import { ParticleField } from './components/backgrounds';
import { ACCENT_BLUE, CHAMPIONS, DARK_BG, EASE_OUT, FAQ_ITEMS, MUTED_GRAY, NOPING_GREEN, SECTION_ALT, SWITCH_REASONS, TEXT_LIGHT, USER_REVIEWS } from './components/constants';
import { GameCarousel, PingSliderCompare, ProofBars, Stars, StatsTicker } from './components/interactive';
import { fadeUp, RevealOnScroll, scaleIn, Section, SectionTitle, staggerContainer } from './components/ui-helpers';

interface EveryoneUsesProps extends DefaultTrialProps {}

export function EveryoneUses({ daysTrial, trialConfigId, trialPageId, isDisabledButton = false }: EveryoneUsesProps) {
  const { t } = useTranslation(['components/trial/lp/v1c']);
  const [modalOpen, setModalOpen] = useState(false);
  const [pingPercent, setPingPercent] = useState(0);
  const pingRef = useRef<HTMLDivElement>(null);
  const pingInView = useInView(pingRef, { once: true, margin: '-40px' });

  useEffect(() => {
    if (!pingInView) return undefined;
    let current = 0;
    const step = () => {
      current += 1;
      setPingPercent(Math.min(current, 80));
      if (current < 80) requestAnimationFrame(step);
    };
    const timer = setTimeout(step, 400);
    return () => clearTimeout(timer);
  }, [pingInView]);

  return (
    <div className={`${bai_jamjuree.className} ${outfit.variable} min-h-screen antialiased`} style={{ background: DARK_BG, color: TEXT_LIGHT, fontFamily: 'var(--font-outfit), sans-serif' }}>
      <Header variant='primary' />

      {/* HERO */}
      <section className='relative overflow-hidden'>
        <ParticleField />

        <div className='pointer-events-none absolute left-1/2 top-1/2 size-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[120px]' style={{ background: `${NOPING_GREEN}08` }} />

        <Section className='relative z-10 pb-8 pt-24 text-center md:pb-12 md:pt-32'>
          <motion.div className='mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5' initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE_OUT as unknown as [number, number, number, number] }}>
            <span className='text-xs font-semibold uppercase tracking-wider' style={{ color: NOPING_GREEN }}>
              {t('hero.badge')}
            </span>
            <span className='text-xs' style={{ color: MUTED_GRAY }}>
              |
            </span>
            <span className='text-xs' style={{ color: MUTED_GRAY }}>
              Sacy, coldzera, BRTT
            </span>
          </motion.div>

          <motion.h1 className='mb-3 text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl md:text-5xl lg:text-6xl' style={{ fontFamily: 'var(--font-bai), sans-serif' }} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1, ease: EASE_OUT as unknown as [number, number, number, number] }}>
            <span className='text-white'>{t('hero.title_prefix')} </span>
            <br className='hidden sm:block' />
            <span style={{ color: NOPING_GREEN }}>{t('hero.title_accent')}</span>
          </motion.h1>

          <motion.p className='mx-auto mb-6 max-w-2xl text-base leading-relaxed sm:text-lg' style={{ color: MUTED_GRAY }} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25, ease: EASE_OUT as unknown as [number, number, number, number] }}>
            {t('hero.subtitle')}
          </motion.p>

          <motion.div ref={pingRef} className='my-4 md:my-6' initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.4, ease: EASE_OUT as unknown as [number, number, number, number] }}>
            <div className='flex items-baseline justify-center gap-2'>
              <span className='text-5xl font-bold tabular-nums leading-none tracking-tighter sm:text-6xl md:text-7xl lg:text-8xl' style={{ fontFamily: 'var(--font-bai), sans-serif', color: NOPING_GREEN }}>
                {pingPercent}%
              </span>
              <span className='text-xl font-bold tracking-tight sm:text-2xl md:text-3xl' style={{ color: `${NOPING_GREEN}B3` }}>
                {t('hero.ping_reduction')}
              </span>
            </div>
            <div className='mt-3 flex items-center justify-center gap-6'>
              <span className='font-mono text-sm' style={{ color: MUTED_GRAY }}>
                <span className='font-bold' style={{ color: ACCENT_BLUE }}>
                  2,000+
                </span>{' '}
                {t('hero.servers')}
              </span>
              <span style={{ color: MUTED_GRAY }}>|</span>
              <span className='font-mono text-sm' style={{ color: MUTED_GRAY }}>
                <span className='font-bold' style={{ color: ACCENT_BLUE }}>
                  150+
                </span>{' '}
                {t('hero.countries')}
              </span>
              <span style={{ color: MUTED_GRAY }}>|</span>
              <span className='font-mono text-sm' style={{ color: MUTED_GRAY }}>
                <span className='font-bold' style={{ color: ACCENT_BLUE }}>
                  3M+
                </span>{' '}
                {t('hero.gamers')}
              </span>
            </div>
          </motion.div>

          <motion.div className='mx-auto max-w-md' initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.6, ease: EASE_OUT as unknown as [number, number, number, number] }}>
            <PingSliderCompare />
          </motion.div>

          <motion.div className='mt-6' initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.8, ease: EASE_OUT as unknown as [number, number, number, number] }}>
            <motion.button onClick={() => setModalOpen(true)} disabled={isDisabledButton} className='inline-block cursor-pointer rounded-2xl border-none px-10 py-4 text-lg font-bold' style={{ background: NOPING_GREEN, color: DARK_BG, boxShadow: `0 0 30px ${NOPING_GREEN}25` }} whileHover={{ scale: 1.03, boxShadow: `0 0 40px ${NOPING_GREEN}40` }} whileTap={{ scale: 0.97 }}>
              {t('hero.cta')}
            </motion.button>
          </motion.div>
        </Section>
      </section>

      {/* STATS TICKER */}
      <StatsTicker />

      {/* CHAMPION ENDORSEMENT WALL */}
      <Section className='py-20 md:py-28'>
        <SectionTitle subtitle={t('champions.subtitle')} title={t('champions.title')} titleAccent={t('champions.title_accent')} description={t('champions.description')} />

        <motion.div className='grid grid-cols-1 gap-6 md:grid-cols-3' variants={staggerContainer} initial='hidden' whileInView='visible' viewport={{ once: true }}>
          {CHAMPIONS.map((champ) => (
            <motion.div key={champ.name} variants={fadeUp} className='group relative cursor-default overflow-hidden rounded-2xl p-6 md:p-8' style={{ background: champ.accentBg }} whileHover={{ y: -6, transition: { duration: 0.3 } }}>
              <div className='absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100' style={{ boxShadow: `inset 0 0 0 1.5px ${champ.accentGlow}40, 0 0 40px ${champ.accentGlow}15` }} />

              <div className='absolute inset-x-6 top-0 h-px' style={{ background: `linear-gradient(90deg, transparent, ${champ.accentGlow}60, transparent)` }} />

              <div className='relative z-10'>
                <div className='mb-6 flex items-center gap-4'>
                  <div className='size-14 shrink-0 overflow-hidden rounded-full border-2' style={{ borderColor: champ.accentGlow }}>
                    <Image src={champ.image} alt={champ.name} width={56} height={56} className='size-full rounded-full object-cover' />
                  </div>
                  <div>
                    <h3 className='text-lg font-bold text-white' style={{ fontFamily: 'var(--font-bai), sans-serif' }}>
                      {champ.name}
                    </h3>
                    <p className='text-sm font-medium' style={{ color: champ.accentGlow }}>
                      {t(champ.title)}
                    </p>
                  </div>
                </div>

                <blockquote className='mb-5 text-[15px] leading-relaxed' style={{ color: '#D1D1D8' }}>
                  &ldquo;{t(champ.quote)}&rdquo;
                </blockquote>

                <div className='flex items-center justify-between'>
                  <span className='text-xs font-semibold uppercase tracking-wider' style={{ color: champ.accentGlow }}>
                    {champ.game}
                  </span>
                  <Stars count={5} />
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </Section>

      {/* BEFORE / AFTER PROOF BARS */}
      <Section className='py-16 md:py-20'>
        <RevealOnScroll className='mb-10 text-center'>
          <h2 className='mb-3 text-2xl font-bold text-white sm:text-3xl' style={{ fontFamily: 'var(--font-bai), sans-serif' }}>
            {t('proof.title')}
          </h2>
          <p className='mx-auto max-w-md text-sm' style={{ color: MUTED_GRAY }}>
            {t('proof.subtitle')}
          </p>
        </RevealOnScroll>

        <ProofBars />
      </Section>

      {/* MASONRY REVIEW WALL */}
      <Section className='py-20 md:py-28'>
        <SectionTitle subtitle={t('reviews.subtitle')} title={t('reviews.title')} titleAccent={t('reviews.title_accent')} description={t('reviews.description')} />

        <motion.div className='columns-1 gap-5 space-y-5 sm:columns-2 lg:columns-3' variants={staggerContainer} initial='hidden' whileInView='visible' viewport={{ once: true, margin: '-50px' }}>
          {USER_REVIEWS.map((review) => (
            <motion.div key={review.name} variants={scaleIn} className='group break-inside-avoid overflow-hidden rounded-xl border border-white/5 transition-all duration-300 hover:border-[#93FF18]/20' whileHover={{ y: -3, transition: { duration: 0.2 } }}>
              <div className='h-px' style={{ background: `linear-gradient(90deg, transparent, ${NOPING_GREEN}4D, transparent)` }} />

              <div className='p-6'>
                <Stars count={review.stars} />
                <p className='mb-5 mt-4 text-sm leading-relaxed' style={{ color: '#D1D1D8' }}>
                  &ldquo;{t(review.quote)}&rdquo;
                </p>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <div className='flex size-7 items-center justify-center rounded-full text-[10px] font-bold' style={{ background: `${NOPING_GREEN}1A`, color: NOPING_GREEN }}>
                      {review.name[0]}
                    </div>
                    <span className='text-sm font-semibold text-white'>{review.name}</span>
                  </div>
                  <span className='rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider' style={{ color: `${ACCENT_BLUE}B3`, background: `${ACCENT_BLUE}0D` }}>
                    {review.game}
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </Section>

      {/* WHY GAMERS SWITCH */}
      <section style={{ background: SECTION_ALT }}>
        <Section className='py-20 md:py-28'>
          <SectionTitle subtitle={t('switch.subtitle')} title={t('switch.title')} titleAccent={t('switch.title_accent')} description={t('switch.description')} />

          <motion.div className='grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3' variants={staggerContainer} initial='hidden' whileInView='visible' viewport={{ once: true }}>
            {SWITCH_REASONS.map((reason) => (
              <motion.div key={reason.title} variants={fadeUp} className='group relative overflow-hidden rounded-2xl border border-white/5 p-7 transition-all duration-300 hover:border-[#93FF18]/15' style={{ background: '#111114' }} whileHover={{ y: -4, transition: { duration: 0.3 } }}>
                <div className='absolute -end-12 -top-12 size-32 rounded-full opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-100' style={{ background: reason.color }} />

                <div className='relative z-10'>
                  <div className='mb-5 flex items-center justify-between'>
                    <div className='flex size-12 items-center justify-center rounded-xl' style={{ background: `${reason.color}12`, color: reason.color }}>
                      <svg viewBox='0 0 24 24' className='size-7' fill='none' stroke='currentColor' strokeWidth={2} strokeLinecap='round' strokeLinejoin='round'>
                        <path d={reason.iconPath} />
                      </svg>
                    </div>
                    <span className='font-mono text-2xl font-bold tabular-nums' style={{ color: reason.color }}>
                      {reason.stat}
                    </span>
                  </div>
                  <h3 className='mb-2 text-lg font-bold text-white' style={{ fontFamily: 'var(--font-bai), sans-serif' }}>
                    {t(reason.title)}
                  </h3>
                  <p className='text-sm leading-relaxed' style={{ color: MUTED_GRAY }}>
                    {t(reason.description)}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <RevealOnScroll className='mt-12 text-center'>
            <motion.button onClick={() => setModalOpen(true)} disabled={isDisabledButton} className='inline-block cursor-pointer rounded-2xl border-none px-10 py-4 text-lg font-bold' style={{ background: NOPING_GREEN, color: DARK_BG, boxShadow: `0 0 30px ${NOPING_GREEN}25` }} whileHover={{ scale: 1.03, boxShadow: `0 0 40px ${NOPING_GREEN}40` }} whileTap={{ scale: 0.97 }}>
              {t('hero.cta')}
            </motion.button>
          </RevealOnScroll>
        </Section>
      </section>

      {/* GAMES CAROUSEL */}
      <Section className='py-20 md:py-28'>
        <SectionTitle subtitle={t('games.subtitle')} title={t('games.title')} titleAccent={t('games.title_accent')} description={t('games.description')} />
        <GameCarousel />
      </Section>

      {/* FAQ */}
      <section style={{ background: SECTION_ALT }}>
        <Section className='py-20 md:py-28'>
          <SectionTitle title={t('faq.title')} description={t('faq.description')} />

          <div className='mx-auto max-w-2xl'>
            <FaqAccordion items={FAQ_ITEMS.map((item) => ({ q: t(item.q), a: t(item.a) }))} accentColor={NOPING_GREEN} />
          </div>
        </Section>
      </section>

      {/* FINAL CTA */}
      <Section className='relative overflow-hidden py-24 text-center md:py-32'>
        <div className='pointer-events-none absolute left-1/2 top-1/2 size-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[100px]' style={{ background: `${NOPING_GREEN}08` }} />

        <RevealOnScroll className='relative z-10'>
          <div className='mb-8 flex items-center justify-center gap-3'>
            <span className='relative flex size-2.5'>
              <span className='absolute inline-flex size-full animate-ping rounded-full opacity-75' style={{ background: NOPING_GREEN }} />
              <span className='relative inline-flex size-2.5 rounded-full' style={{ background: NOPING_GREEN }} />
            </span>
            <span className='text-sm' style={{ color: MUTED_GRAY }}>
              <span className='font-mono font-bold' style={{ color: NOPING_GREEN }}>
                3,000,000+
              </span>{' '}
              {t('final_cta.gamers_counting')}
            </span>
          </div>

          <h2 className='mb-4 text-3xl font-bold text-white sm:text-4xl md:text-5xl' style={{ fontFamily: 'var(--font-bai), sans-serif' }}>
            {t('final_cta.headline')}
            <br />
            <span style={{ color: NOPING_GREEN }}>{t('final_cta.headline_accent')}</span>
          </h2>
          <p className='mx-auto mb-10 max-w-md text-lg' style={{ color: MUTED_GRAY }}>
            {t('final_cta.description')}
          </p>

          <motion.button onClick={() => setModalOpen(true)} disabled={isDisabledButton} className='inline-block cursor-pointer rounded-2xl border-none px-10 py-4 text-lg font-bold' style={{ background: NOPING_GREEN, color: DARK_BG, boxShadow: `0 0 30px ${NOPING_GREEN}25` }} whileHover={{ scale: 1.03, boxShadow: `0 0 40px ${NOPING_GREEN}40` }} whileTap={{ scale: 0.97 }}>
            {t('final_cta.button')}
          </motion.button>
        </RevealOnScroll>
      </Section>

      <Footer text={t('footer', { year: new Date().getFullYear() })} />

      <CreateAccountModal open={modalOpen} onClose={() => setModalOpen(false)} daysTrial={daysTrial} customTrialData={{ custom: trialConfigId, trialPageId }} isDisabledButton={isDisabledButton} formId='v1c-modal-form' />
    </div>
  );
}
